/**
 * Shared Zod schemas + error helpers for form fields whose rules repeat across
 * features (lead capture, Section 8 tenants, lender contacts, …).
 *
 * Keeping one definition per rule means a field's validation message reads the
 * same everywhere the field appears.
 */
import { z } from "zod";

export const EMAIL_ERROR = "Enter a valid email address";

/**
 * A required email address. Trimmed before it reaches the database, so trailing
 * whitespace pasted from a mail client never gets persisted.
 */
export const emailSchema = z.string().trim().email(EMAIL_ERROR);

/**
 * An optional email address: blank (or whitespace-only) is allowed, but any
 * value the user does type must be well-formed. Parses to `null` when blank and
 * a trimmed address otherwise — exactly the shape a nullable DB column wants.
 */
export const optionalEmailSchema = z
  .string()
  .transform((v) => v.trim())
  .refine((v) => v === "" || emailSchema.safeParse(v).success, {
    message: EMAIL_ERROR,
  })
  .transform((v) => (v === "" ? null : v));

/**
 * Validation message for an optional email field, or null when it's blank or
 * valid. Suitable for live per-keystroke feedback.
 */
export function optionalEmailError(value: string): string | null {
  const result = optionalEmailSchema.safeParse(value);
  return result.success
    ? null
    : (result.error.issues[0]?.message ?? EMAIL_ERROR);
}

/**
 * The value to persist for an optional email field: a trimmed address, or null
 * when blank. Returns null for an invalid value too, so callers must gate on
 * {@link optionalEmailError} before submitting.
 */
export function toPersistedEmail(value: string): string | null {
  const result = optionalEmailSchema.safeParse(value);
  return result.success ? result.data : null;
}
